import { Shield } from 'lucide-react';

const LAST_UPDATED = '12 July 2026';

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <section className="text-center py-12">
        <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
          <Shield className="w-4 h-4" />
          Legal
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-gray-500">Last updated: {LAST_UPDATED}</p>
      </section>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 md:p-12 space-y-8 mb-16 prose prose-sm max-w-none">

        <section>
          <p className="text-gray-600 leading-relaxed">
            This Privacy Policy describes how <strong>VarBiz Ltd</strong> ("we", "us", or "our") collects, uses, and protects
            your personal information when you use the <strong>RemindMeOf</strong> service ("Service") available at
            remindmeof.co.uk. By using our Service you agree to the practices described in this policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">1. Who We Are</h2>
          <p className="text-gray-600 leading-relaxed">
            RemindMeOf is operated by <strong>VarBiz Ltd</strong>, a company registered in the United Kingdom. If you have
            any questions about this policy or how we handle your data, please contact us at{' '}
            <a href="mailto:contactus@varbiz.co.uk" className="text-blue-600 hover:underline">contactus@varbiz.co.uk</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
          <p className="text-gray-600 leading-relaxed mb-3">We collect the following categories of personal information:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Account information:</strong> email address, full name, mobile number, and postal address (optional fields).</li>
            <li><strong>Authentication data:</strong> hashed passwords or, if you sign in with Google, your Google account identifier.</li>
            <li><strong>Reminder content:</strong> titles, descriptions, due dates, and notification preferences you enter.</li>
            <li><strong>Usage data:</strong> log data such as IP address, approximate location (country/region/city derived from your IP address), browser type and version, operating system, device type, referring page, pages visited, and timestamps, collected automatically for security and analytics purposes.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
          <p className="text-gray-600 leading-relaxed mb-3">We use your personal information to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Create and manage your account and authenticate you securely.</li>
            <li>Send reminder notifications to your registered email address.</li>
            <li>Send transactional emails such as email verification and password reset links.</li>
            <li>Provide customer support and respond to your enquiries.</li>
            <li>Improve the Service through usage analytics, including understanding new versus returning visitors, approximate geographic reach, and device/browser usage.</li>
            <li>Send you occasional marketing emails about product updates and tips — only if you opted in at sign-up. You can withdraw this consent at any time from your Profile settings or by emailing us at contactus@varbiz.co.uk.</li>
            <li>Comply with legal obligations applicable to VarBiz Ltd.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">4. Legal Basis for Processing (UK/EU Users)</h2>
          <p className="text-gray-600 leading-relaxed">
            Where the UK GDPR or EU GDPR applies, we process your personal data on the following bases: (a) <em>performance of a contract</em> — to provide the Service you signed up for; (b) <em>legitimate interests</em> — for security, fraud prevention, and service improvement; and (c) <em>compliance with legal obligations</em>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">5. Data Sharing</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            We do not sell, rent, or trade your personal information. We share data only with trusted third-party service providers who process it on our behalf:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Cloudflare</strong> — infrastructure and content delivery.</li>
            <li><strong>Resend</strong> — transactional email delivery.</li>
            <li><strong>Google</strong> — OAuth sign-in (if you choose to use it) and, optionally, Google AdSense for advertising.</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            All third-party providers are contractually bound to protect your data and may only use it for the purposes for which it was shared.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">6. Advertising</h2>
          <p className="text-gray-600 leading-relaxed">
            We display advertisements on non-administrative pages via Google AdSense. Google may use cookies and similar technologies to serve ads based on your prior visits to our site and other websites. You can opt out of personalised advertising by visiting{' '}
            <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Ads Settings</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">7. Cookies</h2>
          <p className="text-gray-600 leading-relaxed">
            We use an <strong>httpOnly session cookie</strong> to keep you signed in securely; this is essential and does not require consent. Advertising cookies from Google AdSense are non-essential: we do not load the AdSense script or serve any ads until you accept them via the cookie banner shown on your first visit. You can change your choice at any time using the "Cookie Preferences" link in the footer.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">8. Data Retention</h2>
          <p className="text-gray-600 leading-relaxed">
            We retain your account and reminder data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where we are required to retain it for legal or compliance purposes.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">9. Your Rights</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            Depending on your location, you may have the right to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate data.</li>
            <li>Request deletion of your data ("right to be forgotten").</li>
            <li>Object to or restrict certain processing activities.</li>
            <li>Receive your data in a portable format.</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            To exercise any of these rights, email us at{' '}
            <a href="mailto:contactus@varbiz.co.uk" className="text-blue-600 hover:underline">contactus@varbiz.co.uk</a>.
            We will respond within 30 days.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">10. Security</h2>
          <p className="text-gray-600 leading-relaxed">
            We implement industry-standard security measures including HTTPS encryption in transit, PBKDF2 password hashing, and httpOnly authentication cookies. No method of transmission over the internet is 100% secure; we continuously review and improve our security practices.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">11. Children's Privacy</h2>
          <p className="text-gray-600 leading-relaxed">
            Our Service is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us and we will delete it promptly.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">12. Changes to This Policy</h2>
          <p className="text-gray-600 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page with an updated "Last updated" date. Continued use of the Service after changes constitutes acceptance of the revised policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">13. Contact</h2>
          <p className="text-gray-600 leading-relaxed">
            For any privacy-related questions or requests, please contact VarBiz Ltd at:{' '}
            <a href="mailto:contactus@varbiz.co.uk" className="text-blue-600 hover:underline">contactus@varbiz.co.uk</a>.
          </p>
        </section>

      </div>
    </div>
  );
}
